import { useCallback, useEffect, useRef } from 'react'

// Auto-scroll while a pointer drag nears the edge of the scroll container: how
// close to the top/bottom the pointer must get before scrolling kicks in, and
// the peak scroll speed (px/frame) at the very edge. Speed ramps linearly with
// how far into the hot zone the pointer is, so it eases in.
const AUTO_SCROLL_EDGE = 64
const AUTO_SCROLL_MAX_SPEED = 18

// Nearest vertically scrollable ancestor of `el`. Found by walking up rather
// than hard-coding a class so the lookup survives DOM/markup changes.
function findScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null
  while (node) {
    const overflowY = getComputedStyle(node).overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node
    node = node.parentElement
  }
  return null
}

export interface DragAutoScroll {
  /**
   * Begin auto-scrolling: resolves the scroll container from `anchor` and starts
   * tracking the pointer. Pass the drag's starting `pointerY` (viewport
   * `clientY`) so the first frames use the real position instead of a stale one.
   */
  start: (anchor: HTMLElement | null, pointerY?: number) => void
  /** Stop auto-scrolling and detach all listeners. */
  stop: () => void
}

/**
 * Edge-triggered auto-scroll for a pointer-driven drag (e.g. reordering a list
 * whose target slot is off-screen). While active, an rAF loop nudges the
 * resolved scroll container toward whichever edge the pointer is hugging,
 * scaling speed with hot-zone depth. The drag library's own layout tracking
 * (e.g. framer `layoutScroll`) is responsible for keeping the dragged item
 * under the pointer as the container scrolls.
 *
 * Safe to leave running across renders; it self-cleans on unmount.
 */
export function useDragAutoScroll(): DragAutoScroll {
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const pointerYRef = useRef(0)
  // Whether `pointerYRef` holds a real position for this drag yet. Guards the
  // rAF loop from acting on a stale Y (0 or the previous drag's last position)
  // in the frames between `start` and the first `pointermove`.
  const hasPointerYRef = useRef(false)
  const rafRef = useRef<number | null>(null)
  const isRunningRef = useRef(false)

  const handlePointerMove = useCallback((e: PointerEvent) => {
    pointerYRef.current = e.clientY
    hasPointerYRef.current = true
  }, [])

  const tick = useCallback(() => {
    rafRef.current = null
    if (!isRunningRef.current) {
      return
    }

    const container = scrollContainerRef.current
    if (container && hasPointerYRef.current) {
      const { top, bottom } = container.getBoundingClientRect()
      const y = pointerYRef.current
      const topDepth = AUTO_SCROLL_EDGE - (y - top)
      const bottomDepth = AUTO_SCROLL_EDGE - (bottom - y)
      let delta = 0
      if (topDepth > 0) delta = -AUTO_SCROLL_MAX_SPEED * Math.min(1, topDepth / AUTO_SCROLL_EDGE)
      else if (bottomDepth > 0) delta = AUTO_SCROLL_MAX_SPEED * Math.min(1, bottomDepth / AUTO_SCROLL_EDGE)
      if (delta !== 0) container.scrollTop += delta
    }
    if (isRunningRef.current) {
      rafRef.current = requestAnimationFrame(tick)
    }
  }, [])

  const start = useCallback(
    (anchor: HTMLElement | null, pointerY?: number) => {
      scrollContainerRef.current = findScrollableAncestor(anchor)
      isRunningRef.current = true
      if (pointerY != null) {
        pointerYRef.current = pointerY
        hasPointerYRef.current = true
      } else {
        hasPointerYRef.current = false
      }
      // Detach first so a second start() (overlapping drag lifecycle) can't
      // strand a listener that stop()'s single remove would miss — don't rely
      // on the browser's addEventListener dedup for correctness.
      window.removeEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointermove', handlePointerMove)
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick)
    },
    [handlePointerMove, tick],
  )

  const stop = useCallback(() => {
    isRunningRef.current = false
    window.removeEventListener('pointermove', handlePointerMove)
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    scrollContainerRef.current = null
    hasPointerYRef.current = false
  }, [handlePointerMove])

  // Safety net: tear down the listener/rAF if the consumer unmounts mid-drag.
  useEffect(() => stop, [stop])

  return { start, stop }
}
